import React, { useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { forceCheck } from 'react-lazyload';

import SnapaskLocaleSelection from 'components/SnapaskLocaleSelection';
import SnapaskHeader from 'components/SnapaskHeader';
import SnapaskFooter from 'components/SnapaskFooter';
import AlertBanner from 'components/AlertBanner';
import SnapaskLoader from 'components/SnapaskLoader';
import TrackVisibility from 'components/TrackVisibility';
import useStyles from 'hooks/useStyles';

import {
  REGULAR_CLASS_TOPIC_CLICK,
  REGULAR_CLASS_EPISODE_PLAY,
  REGULAR_CLASS_EPISODE_END,
  REGULAR_CLASS_BANNER_CLICK,
  REGULAR_CLASS_HOME_SCROLL_DEPTH,
} from 'constants/gtmEvent';
import {
  getLiveTopics,
  getLiveTopicsPageSetting,
  getLiveGradeLevels,
  getLiveFilterInfo,
  getRcBannerList,
} from 'reduxModules/live/selector';
import {
  fetchLiveTopics,
  fetchLiveTopicsClear,
  fetchRcBanners,
} from 'reduxModules/live/action';
import { EMPTY_STRING } from 'constants/i18n';
import { RC_HOME } from 'constants/liveEventSections';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { getFetchLiveTopicsLoadingState } from 'reduxModules/actionStatus/selector';
import { buildQuery } from 'utils/query';
import useOnFilterChange from 'routes/live/hooks/useOnFilterChange';
import ToTop from 'components/ToTop';
import PromotionBanner from 'components/PromotionBanner';
import LevelSelector from 'components/LevelSelector';
import Features from 'components/Features';
import Testimonial from 'components/Testimonial';

import { FREE } from '../constants/episodeGroupTypes';
import LiveLayout from '../components/LiveLayout';
import ClassList from './components/ClassList';
import styles from './RcHome.scss';

const RcHome = () => {
  useStyles(styles);
  const { t } = useTranslation(['regularClass']);
  const liveGradeLevels = useSelector(getLiveGradeLevels);
  const bannerList = useSelector(getRcBannerList);
  const onGradeLevelChange = useOnFilterChange('selectedGradeLevel');
  const onSubjectChange = useOnFilterChange('selectedSubject');

  const { isReady: isLiveTopicsReady } = useSelector(
    getFetchLiveTopicsLoadingState,
    shallowEqual,
  );
  const dispatch = useDispatch();
  const liveTopics = useSelector(getLiveTopics);
  const { hasMorePage, page } = useSelector(getLiveTopicsPageSetting);
  const { selectedGradeLevel, selectedSubject } = useSelector(
    getLiveFilterInfo,
  );

  const featuresConfig = useMemo(() => {
    const config = [];
    if (t('feature.notes.title') !== EMPTY_STRING) {
      config.push({
        title: t('feature.notes.title'),
        description: t('feature.notes.description'),
        imageFull: t('feature.notes.imageFull'),
        imageVertical: t('feature.notes.imageVertical'),
        imageHorizontal: t('feature.notes.imageHorizontal'),
        desktopType: 1,
        mobileType: 1,
      });
    }
    if (t('feature.liveqa.title') !== EMPTY_STRING) {
      config.push({
        title: t('feature.liveqa.title'),
        description: t('feature.liveqa.description'),
        imageFull: t('feature.liveqa.imageFull'),
        imageVertical: t('feature.liveqa.imageVertical'),
        imageHorizontal: t('feature.liveqa.imageHorizontal'),
        desktopType: 2,
        mobileType: 2,
      });
    }
    if (t('feature.videoPlayback.title') !== EMPTY_STRING) {
      config.push({
        title: t('feature.videoPlayback.title'),
        description: t('feature.videoPlayback.description'),
        imageFull: t('feature.videoPlayback.imageFull'),
        imageVertical: t('feature.videoPlayback.imageVertical'),
        imageHorizontal: t('feature.videoPlayback.imageHorizontal'),
        desktopType: 3,
        mobileType: 1,
      });
    }

    return config;
  }, [t]);

  const handleSeeMore = useCallback(() => {
    dispatch(fetchLiveTopics({ page: page + 1 }));
  }, [page, dispatch]);

  const handleFilterChange = (value, option) => {
    if (value[0] !== selectedGradeLevel) {
      onGradeLevelChange({ id: value[0], name: option.label });
      buildQuery('gradeLevelId', value[0]);
    }
    if (value[1] !== selectedSubject) {
      onSubjectChange({ id: value[1], name: option.label });
      buildQuery('subjectId', value[1]);
    }
    dispatch(fetchLiveTopicsClear());
    dispatch(fetchLiveTopics());
  };

  const handleLinkClick = (
    { id, name, instructorIdList, instructorNameList },
    index,
  ) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_TOPIC_CLICK,
        data: {
          topicId: id,
          topicName: name,
          gradeLevelName: selectedGradeLevel,
          instructorId: instructorIdList,
          instructorName: instructorNameList,
          section: RC_HOME,
          itemIndex: index + 1,
        },
      }),
    );
  };

  const handleVideoStart = ({ id, name, firstFreeLesson }) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_EPISODE_PLAY,
        data: {
          topicId: id,
          topicName: name,
          episodeId: firstFreeLesson.id,
          episodeName: firstFreeLesson.name,
          duration: firstFreeLesson.offlineVideoDuration,
          episodeStatus: FREE,
          section: RC_HOME,
          watchable: true,
        },
      }),
    );
  };

  const handleVideoEnd = ({ id, name, firstFreeLesson }) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_EPISODE_END,
        data: {
          topicId: id,
          topicName: name,
          episodeId: firstFreeLesson.id,
          episodeName: firstFreeLesson.name,
          duration: firstFreeLesson.offlineVideoDuration,
          episodeStatus: FREE,
          section: RC_HOME,
          watchable: true,
        },
      }),
    );
  };

  const handleBannerClick = ({ id, content }) => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_BANNER_CLICK,
        data: {
          bannerId: id,
          bannerContent: content,
        },
      }),
    );
  };

  const handleInView = id => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_HOME_SCROLL_DEPTH,
        data: {
          'gtm.elementId': id,
          'gtm.visibleRatio': 50,
        },
      }),
    );
  };

  useEffect(() => {
    dispatch(fetchRcBanners());
  }, [selectedGradeLevel]);

  useEffect(() => {
    forceCheck();
  }, []);

  return (
    <LiveLayout>
      <SnapaskHeader />
      <AlertBanner />
      <SnapaskLocaleSelection />
      <div className={styles.container}>
        <div className={styles.decoRect} />
        <div className={styles.decoCircle} />
        <section className={styles.section}>
          <h1 className={styles.title}>{t('title.desktop')}</h1>
          <p className={styles.description}>{t('description')}</p>
          {liveGradeLevels && (
            <TrackVisibility
              id="rc_filter"
              eventName={REGULAR_CLASS_HOME_SCROLL_DEPTH}
            >
              <LevelSelector
                gradeLevelList={liveGradeLevels}
                value={[selectedGradeLevel, selectedSubject]}
                onChange={handleFilterChange}
              />
            </TrackVisibility>
          )}
          {liveTopics && (
            <TrackVisibility
              id="rc_listing"
              eventName={REGULAR_CLASS_HOME_SCROLL_DEPTH}
            >
              <ClassList
                list={liveTopics}
                onLinkClick={handleLinkClick}
                onVideoStart={handleVideoStart}
                onVideoEnd={handleVideoEnd}
                onSeeMoreClick={handleSeeMore}
                showSeeMore={hasMorePage}
              />
            </TrackVisibility>
          )}
          {!isLiveTopicsReady && (
            <div className={styles.loader}>
              <SnapaskLoader />
            </div>
          )}
        </section>
        <TrackVisibility
          id="rc_banner"
          eventName={REGULAR_CLASS_HOME_SCROLL_DEPTH}
        >
          <PromotionBanner
            bannerList={bannerList}
            onClick={handleBannerClick}
          />
        </TrackVisibility>
        <TrackVisibility
          id="rc_testimonial"
          eventName={REGULAR_CLASS_HOME_SCROLL_DEPTH}
        >
          <Testimonial type="regularClass" />
        </TrackVisibility>
        <div className={styles.decoHollowCircle} />
        <Features
          id="rc_feature"
          title={t('feature.title')}
          description={t('feature.description')}
          featuresConfig={featuresConfig}
          inViewConfig={{
            offsetPercentage: 50,
            onInView: handleInView,
          }}
        />
        <ToTop text={t('backToTop')} />
      </div>
      <SnapaskFooter />
    </LiveLayout>
  );
};

export default RcHome;
